import { ActionCard } from "./ActionCard";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useImproveModel } from "@/hooks/useImproveModel";
import { getImproveButtonProps } from "@/hooks/improveButtonProps";
import { useFeatureEnabled, Feature } from "@/hooks/useFeatureEnabled";
import { useReviewBatchCount } from "@/hooks/useReviewBatchCount";
import { useIsTraining } from "@/store";
import { TooltipContainer } from "@scripts/react/components/Tooltip";

interface ImproveActionCardProps {
    disabled?: boolean;
}

export const ImproveActionCard = ({ disabled = false }: ImproveActionCardProps) => {
    const { track } = useAnalytics();
    const { handleImprove, loading } = useImproveModel();
    const isContinuousImprovementEnabled = useFeatureEnabled(Feature.ContinuousImprovement);
    const { count: reviewBatchCount } = useReviewBatchCount();
    const isTraining = useIsTraining();

    if (!isContinuousImprovementEnabled) {
        return null;
    }

    // Disable when batch doesn't exist or has no images (count === 0)
    const hasImagesToReview = (reviewBatchCount ?? 0) > 0;
    const isDisabled = disabled || loading || isTraining || !hasImagesToReview;
    const { title, description, icon } = getImproveButtonProps(isTraining, loading);

    const handleClick = () => {
        track({ event: AnalyticsFlowEvent.IMPROVE_MODEL_CLICKED, data: { step: "review" } });
        handleImprove();
    };

    return (
        <TooltipContainer
            type="titleOnly"
            title="Publish your model first to collect training data."
            placement="left"
            disabled={hasImagesToReview}
            customButton={
                <div>
                    <ActionCard
                        title={title}
                        description={description}
                        icon={icon}
                        type="primary"
                        onClick={isDisabled ? undefined : handleClick}
                        disabled={isDisabled}
                    />
                </div>
            }
        />
    );
};
